import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { projects, projectCategories } from "@/data/projects";
import { videoShowcase } from "@/data/videos";
import VideoEmbed from "@/components/VideoEmbed";
import Reveal from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";

export default function Projects() {
    const [active, setActive] = useState("All");

    const filtered = useMemo(() => {
        if (active === "All") return projects;
        return projects.filter((p) => p.category === active);
    }, [active]);

    return (
        <div data-testid="projects-page" className="bg-charcoal text-cream">
            {/* HEADER */}
            <section className="container-pad mx-auto pt-40 pb-20 md:pt-48 md:pb-24 border-b border-[#262626]">
                <Reveal>
                    <p className="overline mb-8">— Projects</p>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-5xl">
                        A studio of <em className="italic text-gold">considered</em> spaces.
                    </h1>
                    <p className="mt-8 max-w-xl text-base md:text-lg text-[#A3A3A3]">
                        Two hundred and counting. From sky-high penthouses to flagship offices —
                        every project, an artefact of restraint, craft, and obsession.
                    </p>
                </Reveal>
            </section>

            {/* FEATURED VIDEOS */}
            <section className="container-pad mx-auto py-20 md:py-28">
                <Reveal>
                    <p className="overline mb-6">— Case Studies</p>
                    <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-cream mb-10 max-w-3xl">
                        Featured walkthroughs.
                    </h2>
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {videoShowcase.map((v, i) => (
                        <Reveal key={v.videoId} delay={(i % 2) * 140}>
                            <VideoEmbed
                                videoId={v.videoId}
                                subtitle={`Case Study Nº ${String(i + 1).padStart(2, "0")}`}
                                title={v.title}
                                testId={`projects-video-${i + 1}`}
                            />
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* FILTERS + GRID */}
            <section className="container-pad mx-auto pb-28 md:pb-40">
                <div data-testid="project-filters" className="flex flex-wrap gap-3 mb-12 border-t border-[#262626] pt-12">
                    {projectCategories.map((cat) => (
                        <button
                            key={cat}
                            data-testid={`filter-${cat.toLowerCase().replace(/ /g, "-")}`}
                            onClick={() => setActive(cat)}
                            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-medium border transition-colors ${
                                active === cat
                                    ? "bg-gold border-gold text-charcoal"
                                    : "bg-transparent border-[#262626] text-cream hover:border-gold hover:text-gold"
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((p, i) => (
                        <Reveal key={p.id} delay={(i % 6) * 70}>
                            <ProjectCard project={p} index={i} />
                        </Reveal>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <p className="text-center text-[#A3A3A3] py-20">
                        No projects in this category yet.
                    </p>
                )}
            </section>

            {/* CTA */}
            <section className="cta-banner border-t border-[#262626] py-24 md:py-32">
                <div className="container-pad mx-auto text-center">
                    <Reveal>
                        <h2 className="font-display text-4xl md:text-6xl text-cream max-w-3xl mx-auto leading-tight tracking-tight">
                            Have a project in mind?
                        </h2>
                        <Link to="/contact" className="btn-primary mt-10" data-testid="projects-cta">
                            Start a Conversation <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
